import React, { useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  ColumnDef,
  SortingState,
  PaginationState,
  Updater,
} from "@tanstack/react-table";
import { DataTableHeader } from "./DataTableHeader";
import { DataTablePagination } from "./DataTablePagination";
import { DataTableEmpty } from "./DataTableEmpty";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { TableConfig, BaseColumnDef, getAlignClass } from "./types";

interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  config?: TableConfig<T>;
  /**
   * Loading state
   */
  isLoading?: boolean;
  /**
   * Server-side pagination configuration
   */
  serverPagination?: {
    pageIndex: number;
    pageSize: number;
    total: number;
    totalPages: number;
    onPageChange: (page: number) => void;
  };
  /**
   * Server-side sorting configuration
   */
  serverSorting?: {
    sorting: SortingState;
    onSortingChange: (sorting: SortingState) => void;
  };
  /**
   * Custom empty state message
   */
  emptyMessage?: string;
  /**
   * Custom empty state description
   */
  emptyDescription?: string;
  /**
   * Table styles
   */
  styles?: {
    headerBg?: string;
    headerText?: string;
    rowHover?: string;
  };
}

export function DataTable<T>({
  data,
  columns,
  config = {},
  isLoading = false,
  serverPagination,
  serverSorting,
  emptyMessage,
  emptyDescription,
  styles,
}: DataTableProps<T>) {
  const {
    enableSorting = true,
    enablePagination = false,
    pageSize = 10,
    enableZebraStriping = false,
    height = "max-h-[600px]",
    showBorders = true,
    disableHorizontalScroll = false,
  } = config;

  // Client-side sorting state
  const [sorting, setSorting] = React.useState<SortingState>(
    serverSorting?.sorting || []
  );

  // Client-side pagination state
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const prevPageKeyRef = React.useRef<number | null>(null);

  const paginationPageKey = serverPagination
    ? serverPagination.pageIndex
    : pagination.pageIndex;

  React.useEffect(() => {
    if (
      prevPageKeyRef.current !== null &&
      prevPageKeyRef.current !== paginationPageKey
    ) {
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "auto" });
    }
    prevPageKeyRef.current = paginationPageKey;
  }, [paginationPageKey]);

  // Use server-side or client-side sorting
  const sortingState = serverSorting ? serverSorting.sorting : sorting;
  const onSortingChange = serverSorting
    ? (updater: Updater<SortingState>) => {
        const newSorting =
          typeof updater === "function" ? updater(sortingState) : updater;
        serverSorting.onSortingChange(newSorting);
      }
    : setSorting;

  // Table instance
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting && !serverSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel:
      enablePagination && !serverPagination
        ? getPaginationRowModel()
        : undefined,
    manualSorting: !!serverSorting,
    manualPagination: !!serverPagination,
    state: {
      sorting: sortingState,
      pagination: serverPagination
        ? {
            pageIndex: serverPagination.pageIndex - 1,
            pageSize: serverPagination.pageSize,
          }
        : pagination,
    },
    onSortingChange,
    onPaginationChange: setPagination,
    pageCount: serverPagination?.totalPages,
  });

  // Memoize row data
  const rows = useMemo(() => table.getRowModel().rows, [table.getRowModel()]);

  return (
    <div
      className={`overflow-hidden bg-white rounded-lg border ${
        showBorders ? "border-gray-200" : "border-transparent"
      } shadow-sm`}
    >
      <div
        ref={scrollContainerRef}
        className={`${disableHorizontalScroll ? "overflow-x-hidden" : "overflow-x-auto"} ${height} overflow-y-auto`}
      >
        <table className="min-w-full divide-y divide-gray-200">
          <DataTableHeader
            headerGroups={table.getHeaderGroups()}
            styles={styles}
          />
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <DataTableSkeleton
                rows={5}
                columns={columns.length}
              />
            ) : rows.length === 0 ? (
              <DataTableEmpty
                message={emptyMessage}
                description={emptyDescription}
              />
            ) : (
              rows.map((row, idx) => {
                const isEven = idx % 2 === 0;
                return (
                  <tr
                    key={row.id}
                    className={`
                      ${enableZebraStriping && !isEven ? "bg-gray-50/50" : "bg-white"}
                      ${styles?.rowHover || "hover:bg-gray-50"}
                      transition-colors duration-150
                      border-b border-gray-100
                    `}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const columnDef = cell.column.columnDef as BaseColumnDef<T>;
                      return (
                        <td
                          key={cell.id}
                          className={`
                            ${columnDef.cellClassName || ""}
                            ${getAlignClass(columnDef.align)}
                            px-6 py-4
                            whitespace-nowrap
                            text-sm
                            text-gray-700
                          `}
                          style={{
                            width: cell.column.getSize() !== 150
                              ? cell.column.getSize()
                              : undefined,
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {(enablePagination || serverPagination) && (
        <DataTablePagination
          table={table}
          serverPagination={serverPagination}
        />
      )}
    </div>
  );
}

